/**
 * Detects the dialling code (e.g. "+91") for the current visitor's location.
 *
 * Same providers as the user app's CountrySelector (metawallet-frontend):
 *   1. ipapi.co   (IP lookup, returns the calling code directly)
 *   2. ipinfo.io  (IP lookup, returns the ISO country)
 *   3. the browser's time zone (offline fallback, no network)
 * The result is cached for the browser session so the lookup runs once.
 */

const CACHE_KEY = "detectedCountryCode";
const LOOKUP_TIMEOUT_MS = 4000;

// ISO 3166-1 alpha-2 → international dialling code
export const ISO_TO_DIAL_CODE = {
  AD: "+376", AE: "+971", AF: "+93", AG: "+1", AL: "+355", AM: "+374", AO: "+244", AR: "+54",
  AT: "+43", AU: "+61", AZ: "+994", BA: "+387", BB: "+1", BD: "+880", BE: "+32", BF: "+226",
  BG: "+359", BH: "+973", BI: "+257", BJ: "+229", BN: "+673", BO: "+591", BR: "+55", BS: "+1",
  BT: "+975", BW: "+267", BY: "+375", BZ: "+501", CA: "+1", CD: "+243", CF: "+236", CG: "+242",
  CH: "+41", CI: "+225", CK: "+682", CL: "+56", CM: "+237", CN: "+86", CO: "+57", CR: "+506",
  CU: "+53", CV: "+238", CY: "+357", CZ: "+420", DE: "+49", DJ: "+253", DK: "+45", DM: "+1",
  DO: "+1", DZ: "+213", EC: "+593", EE: "+372", EG: "+20", ER: "+291", ES: "+34", ET: "+251",
  FI: "+358", FJ: "+679", FM: "+691", FR: "+33", GA: "+241", GB: "+44", GD: "+1", GE: "+995",
  GH: "+233", GM: "+220", GN: "+224", GQ: "+240", GR: "+30", GT: "+502", GW: "+245", GY: "+592",
  HK: "+852", HN: "+504", HR: "+385", HT: "+509", HU: "+36", ID: "+62", IE: "+353", IL: "+972",
  IN: "+91", IQ: "+964", IR: "+98", IS: "+354", IT: "+39", JM: "+1", JO: "+962", JP: "+81",
  KE: "+254", KG: "+996", KH: "+855", KI: "+686", KM: "+269", KN: "+1", KP: "+850", KR: "+82",
  KW: "+965", KZ: "+7", LA: "+856", LB: "+961", LC: "+1", LI: "+423", LK: "+94", LR: "+231",
  LS: "+266", LT: "+370", LU: "+352", LV: "+371", LY: "+218", MA: "+212", MC: "+377", MD: "+373",
  ME: "+382", MG: "+261", MH: "+692", MK: "+389", ML: "+223", MM: "+95", MN: "+976", MO: "+853",
  MR: "+222", MT: "+356", MU: "+230", MV: "+960", MW: "+265", MX: "+52", MY: "+60", MZ: "+258",
  NA: "+264", NC: "+687", NE: "+227", NG: "+234", NI: "+505", NL: "+31", NO: "+47", NP: "+977",
  NR: "+674", NU: "+683", NZ: "+64", OM: "+968", PA: "+507", PE: "+51", PF: "+689", PG: "+675",
  PH: "+63", PK: "+92", PL: "+48", PS: "+970", PT: "+351", PW: "+680", PY: "+595", QA: "+974",
  RO: "+40", RS: "+381", RU: "+7", RW: "+250", SA: "+966", SB: "+677", SC: "+248", SD: "+249",
  SE: "+46", SG: "+65", SI: "+386", SK: "+421", SL: "+232", SM: "+378", SN: "+221", SO: "+252",
  SR: "+597", SS: "+211", SV: "+503", SY: "+963", SZ: "+268", TD: "+235", TG: "+228", TH: "+66",
  TJ: "+992", TL: "+670", TM: "+993", TN: "+216", TO: "+676", TR: "+90", TT: "+1", TV: "+688",
  TW: "+886", TZ: "+255", UA: "+380", UG: "+256", US: "+1", UY: "+598", UZ: "+998", VA: "+39",
  VC: "+1", VE: "+58", VN: "+84", VU: "+678", WF: "+681", WS: "+685", YE: "+967", ZA: "+27",
  ZM: "+260", ZW: "+263",
};

// Offline fallback: common IANA time zones → ISO country
const TIME_ZONE_TO_ISO = {
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Dubai": "AE", "Asia/Muscat": "OM",
  "Asia/Qatar": "QA", "Asia/Bahrain": "BH", "Asia/Kuwait": "KW", "Asia/Riyadh": "SA",
  "Asia/Karachi": "PK", "Asia/Dhaka": "BD", "Asia/Kathmandu": "NP", "Asia/Colombo": "LK",
  "Asia/Singapore": "SG", "Asia/Kuala_Lumpur": "MY", "Asia/Bangkok": "TH", "Asia/Jakarta": "ID",
  "Asia/Manila": "PH", "Asia/Ho_Chi_Minh": "VN", "Asia/Hong_Kong": "HK", "Asia/Shanghai": "CN",
  "Asia/Tokyo": "JP", "Asia/Seoul": "KR", "Asia/Tehran": "IR", "Asia/Baghdad": "IQ",
  "Asia/Amman": "JO", "Asia/Beirut": "LB", "Asia/Jerusalem": "IL", "Europe/Istanbul": "TR",
  "Europe/London": "GB", "Europe/Dublin": "IE", "Europe/Paris": "FR", "Europe/Berlin": "DE",
  "Europe/Madrid": "ES", "Europe/Rome": "IT", "Europe/Amsterdam": "NL", "Europe/Zurich": "CH",
  "Europe/Moscow": "RU", "Africa/Cairo": "EG", "Africa/Lagos": "NG", "Africa/Johannesburg": "ZA",
  "Africa/Nairobi": "KE", "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Los_Angeles": "US", "America/Toronto": "CA", "America/Sao_Paulo": "BR",
  "America/Mexico_City": "MX", "Australia/Sydney": "AU", "Australia/Melbourne": "AU",
  "Pacific/Auckland": "NZ",
};

export const getCountryName = (iso) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(iso) || iso;
  } catch (e) {
    return iso;
  }
};

// [{ iso, name, dialCode }] sorted by name — for country dropdowns
export const COUNTRY_OPTIONS = Object.keys(ISO_TO_DIAL_CODE)
  .map((iso) => ({ iso, name: getCountryName(iso), dialCode: ISO_TO_DIAL_CODE[iso] }))
  .sort((a, b) => a.name.localeCompare(b.name));

const fetchJson = async (url) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
};

const normalizeIso = (code) => {
  const iso = String(code || "").toUpperCase();
  return iso === "UAE" ? "AE" : iso;
};

const buildResult = (iso, source, dialCode) => {
  const dial = dialCode || ISO_TO_DIAL_CODE[iso];
  return dial ? { iso, dialCode: dial, countryName: getCountryName(iso), source } : null;
};

const fromIpapi = async () => {
  const data = await fetchJson("https://ipapi.co/json/");
  const iso = normalizeIso(data?.country_code);
  const dial = /^\+\d+$/.test(data?.country_calling_code || "") ? data.country_calling_code : null;
  return iso ? buildResult(iso, "location", dial) : null;
};

const fromIpinfo = async () => {
  const data = await fetchJson("https://ipinfo.io/json");
  const iso = normalizeIso(data?.country);
  return iso ? buildResult(iso, "location") : null;
};

const fromTimeZone = () => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const iso = TIME_ZONE_TO_ISO[timeZone];
    return iso ? buildResult(iso, "timezone") : null;
  } catch (e) {
    return null;
  }
};

/**
 * @returns {Promise<{ iso: string, dialCode: string, countryName: string, source: "location"|"timezone" } | null>}
 */
export const detectCountryCode = async () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (e) {}

  let result = null;
  for (const lookup of [fromIpapi, fromIpinfo]) {
    try {
      result = await lookup();
      if (result) break;
    } catch (error) {
      console.log("Country lookup failed, trying next provider:", error?.message);
    }
  }
  if (!result) result = fromTimeZone();

  if (result) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
    } catch (e) {}
  }
  return result;
};
